/**
 * System Status Dashboard
 * 
 * Provides comprehensive monitoring of the social media posting system,
 * including edge function status, processing queue, and system health metrics.
 */

import React, { useState } from 'react'
import { format, formatDistanceToNow, isAfter } from 'date-fns'
import { 
  RefreshCw, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Activity,
  Send,
  TrendingUp,
  Wifi,
  WifiOff,
  Database,
  Zap,
  BarChart3,
  Calendar,
  PlayCircle,
  Loader2
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import SystemHealthCard, { HealthIndicator } from '@/components/social/SystemHealthCard'
import { useSystemHealth, useManualProcess } from '@/hooks/useSystemHealth'
import { POST_STATUS } from '@/constants/socialPostStatus'

export default function SystemStatus() {
  const { data, isLoading, error, refetch, isConnected } = useSystemHealth()
  const manualProcess = useManualProcess()
  const [isRefreshing, setIsRefreshing] = useState(false)

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await refetch()
    setTimeout(() => setIsRefreshing(false), 1000)
  }

  const handleManualProcess = async () => {
    try {
      await manualProcess.mutateAsync()
    } catch (error) {
      console.error('Manual process failed:', error)
    }
  }

  // Determine overall system health
  const getOverallHealth = () => {
    if (!data) return 'unknown'
    
    const hasErrors = data.edgeFunction.lastRunStatus === 'error' || 
                     data.edgeFunction.errors.length > 0 ||
                     data.metrics.failedPosts > 0 ||
                     data.queue.overdue > 5

    const hasWarnings = data.edgeFunction.isOverdue ||
                       data.queue.overdue > 0 ||
                       data.metrics.successRate < 90 ||
                       data.queue.processing > 10

    if (hasErrors) return 'error'
    if (hasWarnings) return 'warning'
    return 'healthy'
  }

  const overallHealth = getOverallHealth()

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">System Status</h1>
          <p className="text-muted-foreground">
            Monitor the health of your social media posting system
          </p>
        </div>

        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error Loading System Status</AlertTitle>
          <AlertDescription>
            {error.message || 'Unable to fetch system health data. Please try again.'}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">System Status</h1>
          <p className="text-muted-foreground">
            Monitor the health of your social media posting system
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleManualProcess}
            disabled={manualProcess.isPending}
          >
            {manualProcess.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <PlayCircle className="mr-2 h-4 w-4" />
                Manual Process
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={cn(
              "h-4 w-4",
              isRefreshing && "animate-spin"
            )} />
          </Button>
        </div>
      </div>

      {/* Overall System Health */}
      <Card className={cn(
        'border-2',
        overallHealth === 'healthy' && 'border-green-500',
        overallHealth === 'warning' && 'border-yellow-500',
        overallHealth === 'error' && 'border-red-500',
        overallHealth === 'unknown' && 'border-gray-500'
      )}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Activity className="h-6 w-6" />
              <div>
                <CardTitle>Overall System Health</CardTitle>
                <CardDescription>
                  Real-time monitoring of all system components
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <HealthIndicator 
                status={isConnected ? 'healthy' : 'error'} 
                label={isConnected ? 'Connected' : 'Disconnected'}
                size="md"
              />
              {data && (
                <Badge variant={
                  overallHealth === 'healthy' ? 'default' :
                  overallHealth === 'warning' ? 'secondary' :
                  overallHealth === 'error' ? 'destructive' : 'outline'
                }>
                  {overallHealth.charAt(0).toUpperCase() + overallHealth.slice(1)}
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {isLoading ? (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-[200px]" />
          ))}
        </div>
      ) : data ? (
        <>
          {/* Main Status Grid */}
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {/* Edge Function Status */}
            <SystemHealthCard
              title="Edge Function"
              description="Automated post processor"
              status={
                data.edgeFunction.lastRunStatus === 'error' ? 'error' :
                data.edgeFunction.isOverdue ? 'warning' :
                data.edgeFunction.lastRunStatus === 'success' ? 'healthy' : 'unknown'
              }
              icon={<Zap className="h-5 w-5" />}
              metrics={[
                {
                  label: 'Last Run',
                  value: data.edgeFunction.lastRun 
                    ? formatDistanceToNow(data.edgeFunction.lastRun, { addSuffix: true })
                    : 'Never',
                  status: data.edgeFunction.isOverdue ? 'error' : 'good'
                },
                {
                  label: 'Posts Processed',
                  value: data.edgeFunction.postsProcessed,
                  status: data.edgeFunction.postsProcessed > 0 ? 'good' : 'warning'
                }
              ]}
              details={
                <div className="space-y-2">
                  {data.edgeFunction.nextRun && (
                    <p className="text-sm">
                      Next run: {format(data.edgeFunction.nextRun, 'h:mm a')}
                    </p>
                  )}
                  {data.edgeFunction.errors.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-red-600">Recent Errors:</p>
                      {data.edgeFunction.errors.map((error, i) => (
                        <p key={i} className="text-xs text-red-600">{error}</p>
                      ))}
                    </div>
                  )}
                </div>
              }
            />

            {/* Processing Queue */}
            <SystemHealthCard
              title="Processing Queue"
              description="Posts awaiting processing"
              status={
                data.queue.overdue > 5 ? 'error' :
                data.queue.overdue > 0 || data.queue.processing > 10 ? 'warning' :
                'healthy'
              }
              icon={<Clock className="h-5 w-5" />}
              metrics={[
                {
                  label: 'Scheduled',
                  value: data.queue.scheduled,
                  status: data.queue.scheduled > 50 ? 'warning' : 'good'
                },
                {
                  label: 'Processing',
                  value: data.queue.processing,
                  status: data.queue.processing > 10 ? 'warning' : 'good'
                }
              ]}
              details={
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Next Hour</span>
                    <span className="font-medium">{data.queue.nextHour} posts</span>
                  </div>
                  {data.queue.overdue > 0 && (
                    <div className="flex justify-between text-sm text-red-600">
                      <span>Overdue</span>
                      <span className="font-medium">{data.queue.overdue} posts</span>
                    </div>
                  )}
                </div>
              }
            />

            {/* System Performance */}
            <SystemHealthCard
              title="Performance"
              description="Processing efficiency"
              status={
                data.metrics.successRate < 80 ? 'error' :
                data.metrics.successRate < 90 ? 'warning' :
                'healthy'
              }
              icon={<BarChart3 className="h-5 w-5" />}
              metrics={[
                {
                  label: 'Success Rate',
                  value: `${data.metrics.successRate}%`,
                  status: data.metrics.successRate >= 90 ? 'good' : 
                          data.metrics.successRate >= 80 ? 'warning' : 'error'
                },
                {
                  label: 'Avg Time',
                  value: `${(data.metrics.averageProcessingTime / 1000).toFixed(1)}s`,
                  status: data.metrics.averageProcessingTime < 5000 ? 'good' : 'warning'
                }
              ]}
              details={
                data.metrics.failedPosts > 0 && (
                  <p className="text-sm text-red-600">
                    {data.metrics.failedPosts} failed posts need attention
                  </p>
                )
              }
            />

            {/* Connection Status */}
            <SystemHealthCard
              title="Connections"
              description="System connectivity"
              status={
                data.connection.supabase === 'connected' && 
                data.connection.realtime === 'connected' ? 'healthy' :
                data.connection.supabase === 'disconnected' ||
                data.connection.realtime === 'disconnected' ? 'error' : 'warning'
              }
              icon={<Wifi className="h-5 w-5" />}
              metrics={[
                {
                  label: 'Database',
                  value: data.connection.supabase,
                  status: data.connection.supabase === 'connected' ? 'good' : 'error'
                },
                {
                  label: 'Real-time',
                  value: data.connection.realtime,
                  status: data.connection.realtime === 'connected' ? 'good' : 'error'
                }
              ]}
              details={
                data.connection.lastSync && (
                  <p className="text-sm">
                    Last sync: {formatDistanceToNow(data.connection.lastSync, { addSuffix: true })}
                  </p>
                )
              }
            />

            {/* Post Volume */}
            <SystemHealthCard
              title="Post Volume"
              description="Publishing activity"
              status="healthy"
              icon={<Send className="h-5 w-5" />}
              metrics={[
                {
                  label: 'Today',
                  value: data.metrics.postsToday,
                  trend: 'neutral'
                },
                {
                  label: 'This Week',
                  value: data.metrics.postsThisWeek,
                  trend: 'neutral'
                }
              ]}
              details={
                <div className="flex justify-between text-sm">
                  <span>This Month</span>
                  <span className="font-medium">{data.metrics.postsThisMonth} posts</span>
                </div>
              }
            />

            {/* System Alerts */}
            <SystemHealthCard
              title="Active Alerts"
              description="Issues requiring attention"
              status={
                data.metrics.failedPosts > 0 || data.queue.overdue > 5 ? 'error' :
                data.queue.overdue > 0 || data.edgeFunction.isOverdue ? 'warning' :
                'healthy'
              }
              icon={<AlertTriangle className="h-5 w-5" />}
              details={
                <div className="space-y-2">
                  {data.edgeFunction.isOverdue && (
                    <Alert className="p-2">
                      <AlertDescription className="text-xs">
                        Edge function is overdue for execution
                      </AlertDescription>
                    </Alert>
                  )}
                  {data.queue.overdue > 0 && (
                    <Alert className="p-2">
                      <AlertDescription className="text-xs">
                        {data.queue.overdue} posts are past their scheduled time
                      </AlertDescription>
                    </Alert>
                  )}
                  {data.metrics.failedPosts > 0 && (
                    <Alert className="p-2">
                      <AlertDescription className="text-xs">
                        {data.metrics.failedPosts} posts failed to publish
                      </AlertDescription>
                    </Alert>
                  )}
                  {!data.edgeFunction.isOverdue && data.queue.overdue === 0 && data.metrics.failedPosts === 0 && (
                    <p className="text-sm text-green-600">No active alerts</p>
                  )}
                </div>
              }
            />
          </div>

          {/* Recent Activity Log */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Recent Activity</CardTitle>
                  <CardDescription>
                    Latest status changes and system events
                  </CardDescription>
                </div>
                <Badge variant="outline">
                  {data.recentActivity.length} events
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-3">
                  {data.recentActivity.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No recent activity
                    </p>
                  ) : (
                    data.recentActivity.map((activity) => (
                      <div
                        key={activity.id}
                        className="flex items-start gap-3 pb-3 border-b last:border-0"
                      >
                        <div className={cn(
                          'mt-1 p-1 rounded-full',
                          activity.new_status === POST_STATUS.POSTED && 'bg-green-100 dark:bg-green-950',
                          activity.new_status === POST_STATUS.ERROR && 'bg-red-100 dark:bg-red-950',
                          activity.new_status === POST_STATUS.PROCESSING && 'bg-blue-100 dark:bg-blue-950',
                          activity.new_status === POST_STATUS.SCHEDULED && 'bg-yellow-100 dark:bg-yellow-950',
                          activity.new_status === POST_STATUS.DRAFT && 'bg-gray-100 dark:bg-gray-950'
                        )}>
                          {activity.new_status === POST_STATUS.POSTED && (
                            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                          )}
                          {activity.new_status === POST_STATUS.ERROR && (
                            <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                          )}
                          {activity.new_status === POST_STATUS.PROCESSING && (
                            <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          )}
                          {activity.new_status === POST_STATUS.SCHEDULED && (
                            <Calendar className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                          )}
                          {activity.new_status === POST_STATUS.DRAFT && (
                            <Activity className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="text-sm font-medium">
                            {activity.caption_preview}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>
                              {formatDistanceToNow(new Date(activity.changed_at), { addSuffix: true })}
                            </span>
                            <span>•</span>
                            <Badge variant="outline" className="text-xs">
                              {activity.new_status}
                            </Badge>
                          </div>
                          {activity.error_message && (
                            <p className="text-xs text-red-600 mt-1">
                              Error: {activity.error_message}
                            </p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  )
}